import { createSelector } from 'reselect';

import { sortGroupTables } from '../../utils/tablesSorting';
import {
  languageSelector,
  sortTypeSelector,
  filtersSelector,
  sortBJGroupConfigSelector,
  excludedGamesSelector,
  excludedTablesSelector,
} from './config';
import { tablesSelector, favoriteSelector, recentSelector } from './tables';

export const baseCategoriesSelector = (state) => state.categories;

// Filter out excluded tables and games
export const categoriesWithExcludedTables = createSelector(
  baseCategoriesSelector,
  excludedTablesSelector,
  excludedGamesSelector,
  tablesSelector,
  (categories, excludedTables, excludedGames, tables) => categories.map((category) => ({
    ...category,
    tables: category.tables.filter((tableId) => {
      const table = tables[tableId];

      return !(
        excludedTables.includes(table?.tableId)
          || excludedGames.includes(table?.gameId)
      );
    }),
  }))
);

// Filter out empty categories
// and add `hasNew` if it contains new tables that are active
export const categoriesSelector = createSelector(
  categoriesWithExcludedTables,
  tablesSelector,
  (categories, tables) => categories
    .filter((category) => category.tables.length > 0)
    .filter((category) => category.tables.some((tableId) => tables[tableId]))
    .map((category) => {
      const hasNew = category.tables.some(
        (tableId) => tables[tableId]?.active && tables[tableId]?.isNew
      );

      return { ...category, hasNew };
    })
);

export const firstAvailableCategorySelector = createSelector(
  categoriesSelector,
  (categories) => categories.find((category) => category.tables.length > 0)
);

export const currentTablesSelector = createSelector(
  tablesSelector,
  categoriesSelector,
  (_, id) => id,
  favoriteSelector,
  recentSelector,
  languageSelector,
  sortTypeSelector,
  filtersSelector,
  sortBJGroupConfigSelector,
  excludedGamesSelector,
  excludedTablesSelector,
  (
    tables,
    categories,
    id,
    favorite,
    recent,
    language,
    sortType,
    filters,
    bjSortConfig,
    excludedGames,
    excludedTables
  ) => {
    // eslint-disable-next-line eqeqeq
    const category = categories.find((cat) => cat.id == id);

    if (!id || !category || Object.keys(tables).length === 0) return [];

    return sortGroupTables({
      groupId: id,
      groupTables: category.tables.filter((tableId) => Boolean(tables[tableId])),
      tables,
      favorite,
      recent,
      language,
      sortType,
      filters,
      bjSortConfig,
      excludedGames,
      excludedTables,
    }).map(Number);
  }
);
